import { Button } from "@atgis/ui";
import { ArrowRight, ShieldCheck, Layers, MapPinned } from "lucide-react";

export function Hero() {
  return (
    <section className="relative overflow-hidden border-b border-border/60">
      <div className="absolute inset-0 grid-bg pointer-events-none" aria-hidden />
      <div className="container relative grid gap-14 py-20 md:py-28 lg:grid-cols-[1.1fr_1fr] lg:py-32">
        <div className="flex flex-col items-start gap-6">
          <span className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-card/70 px-3 py-1 text-xs font-medium text-muted-foreground backdrop-blur">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[hsl(var(--project-master))]" />
            RADGIS — CBS, harita mühendisliği ve saha için tek platform
          </span>

          <h1 className="text-balance text-4xl font-semibold tracking-tight md:text-6xl">
            Saha, ofis ve yönetim için <span className="gradient-text">tek bir CBS omurgası</span>.
          </h1>

          <p className="max-w-xl text-pretty text-base text-muted-foreground md:text-lg">
            RADGIS Platform; Mevcut Altyapı/Altyapı Projesi hiyerarşisi, kuruma özel veri modeli,
            ekip ve yetki yönetimi, harita üstü görev panosu ve entegre CAD ortamıyla altyapı
            kurumlarını, harita ve CBS mühendislik ofislerini ve sahadaki yüklenicileri aynı
            omurgada buluşturur.
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <Button size="lg" asChild>
              <a href="#iletisim">
                Demo İste <ArrowRight />
              </a>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <a href="#urun">Ürünü Keşfet</a>
            </Button>
          </div>

          <dl className="mt-6 grid w-full grid-cols-1 gap-4 sm:grid-cols-3">
            {[
              { icon: Layers, k: "Kurum veri modeli", v: "Katman ve öznitelikler kuruma özel" },
              {
                icon: MapPinned,
                k: "Mevcut Altyapı / Altyapı Projesi",
                v: "Kontrollü değişiklik akışı",
              },
              {
                icon: ShieldCheck,
                k: "Veritabanı seviyesinde yetki",
                v: "Yetkisiz veriye erişim teknik olarak imkânsız",
              },
            ].map(({ icon: Icon, k, v }) => (
              <div
                key={k}
                className="rounded-xl border border-border/60 bg-card/60 p-4 backdrop-blur"
              >
                <dt className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  <Icon className="h-3.5 w-3.5" /> {k}
                </dt>
                <dd className="mt-1 text-sm font-medium text-foreground">{v}</dd>
              </div>
            ))}
          </dl>
        </div>

        <HeroPreview />
      </div>
    </section>
  );
}

function HeroPreview() {
  return (
    <div className="relative">
      <div className="absolute -inset-6 -z-10 rounded-[2rem] bg-gradient-to-br from-primary/15 via-[hsl(155_55%_38%)]/20 to-[hsl(165_65%_50%)]/15 blur-2xl" />
      <div className="overflow-hidden rounded-2xl border border-border/70 bg-card shadow-soft">
        <div className="flex items-center justify-between border-b border-border/60 bg-muted/30 px-4 py-3">
          <div className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-destructive/70" />
            <span className="h-2.5 w-2.5 rounded-full bg-amber-400/80" />
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-500/80" />
          </div>
          <div className="rounded-md border border-border/60 bg-background/50 px-2.5 py-1 text-xs text-muted-foreground">
            radgis.app / projeler
          </div>
          <div className="text-xs text-muted-foreground">v1.0</div>
        </div>

        <div className="grid grid-cols-[140px_1fr]">
          <aside className="border-r border-border/60 bg-sidebar p-3 text-sidebar-foreground">
            {[
              "Projeler",
              "Veri Modeli",
              "Partnerler",
              "Workspaces",
              "Üyeler",
              "Ekipler",
              "Görev Panosu",
            ].map((label, idx) => (
              <div
                key={label}
                className={
                  "mb-1 rounded-md px-2 py-1.5 text-xs " +
                  (idx === 0
                    ? "bg-sidebar-accent text-sidebar-accent-foreground"
                    : "text-sidebar-foreground/70")
                }
              >
                {label}
              </div>
            ))}
          </aside>

          <div className="space-y-3 p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">Projeler</div>
              <div className="rounded-md border border-border/60 bg-background px-2 py-1 text-xs text-muted-foreground">
                Mevcut Altyapı · Altyapı Projesi · CAD
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <PreviewCard
                tone="master"
                title="Akarsu İçme Suyu"
                sub="Veri modeli: su-şebekesi · 142 sub"
              />
              <PreviewCard
                tone="sub"
                title="Bölge 03 Saha"
                sub="Altyapı Projesi: Akarsu · onay bekliyor"
                pending
              />
              <PreviewCard
                tone="master"
                title="Marmara Doğalgaz"
                sub="Veri modeli: gaz-şebekesi · 87 sub"
              />
              <PreviewCard
                tone="plain"
                title="Kampüs Etüdü"
                sub="Workspace: RADGIS R&D · Serbest"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PreviewCard({
  tone,
  title,
  sub,
  pending,
}: {
  tone: "master" | "sub" | "plain";
  title: string;
  sub: string;
  pending?: boolean;
}) {
  const dot =
    tone === "master"
      ? "bg-[hsl(var(--project-master))]"
      : tone === "sub"
        ? "bg-[hsl(var(--project-sub))]"
        : "bg-[hsl(var(--project-plain))]";
  const label = tone === "master" ? "Mevcut Altyapı" : tone === "sub" ? "Altyapı Projesi" : "CAD";
  return (
    <div className="rounded-lg border border-border/60 bg-background/70 p-3">
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
          {label}
        </span>
        {pending ? (
          <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-300">
            onayda
          </span>
        ) : null}
      </div>
      <div className="mt-2 text-sm font-medium">{title}</div>
      <div className="text-xs text-muted-foreground">{sub}</div>
    </div>
  );
}
